import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CONCERN_CODES, MOOD_SCALE, TOPIC_TAGS } from '../../lib/constants';
import { useI18n } from '../../i18n';
import { useAnnounce, VisuallyHidden } from '../../ui/a11y';
import { Bars, Pill, ScoreValue, Suppressed } from '../../ui/DataBits';
import { MoodSplit } from '../../ui/MoodSplit';
import { Dialog } from '../../ui/Dialog';
import { QrCode } from '../../ui/QrCode';
import { SlideToEnd } from '../../components/SlideToEnd';
import { bandOf } from '../../ui/score';
import { TeacherFrame } from './TeacherFrame';
import { ActOnResults } from './ActOnResults';
import { errorText, rpc, type AppError, type Breakdown, type ConcernBreakdown, type LiveData, type SessionResults } from './api';

// One check-in: while it's open, the conduct view (I2: projector code + QR,
// live count, extend, close); once it has ended, the reflect view (I3:
// results, prediction vs result, "You said, we did", private notes).
export function SessionPage() {
  const { t } = useI18n();
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const [live, setLive] = useState<LiveData | null>(null);
  const [err, setErr] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<LiveData>('teacher_session_live', { p_session_id: sessionId });
    if (r.error) setErr(r.error);
    else setLive(r.data);
  }, [sessionId]);
  useEffect(() => {
    load();
  }, [load]);

  const title = live ? (live.finished ? t['ts.reflectTitle'](live.class_label) : t['ts.conductTitle'](live.class_label)) : t['ts.sessionTitle'];
  return (
    <TeacherFrame title={title} active="classes">
      {() =>
        err ? (
          <div className="panel p5">
            <p role="alert">{errorText(t, err)}</p>
            <p><Link className="ts-link" to="/teacher">← {t['teacher.title']}</Link></p>
          </div>
        ) : !live ? (
          <p role="status">{t.loading}</p>
        ) : live.finished ? (
          <Reflect sessionId={sessionId} classId={live.class_id} />
        ) : (
          <Conduct live={live} setLive={setLive} reload={load} />
        )
      }
    </TeacherFrame>
  );
}

function useCountdown(until: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return Math.max(0, Math.round((new Date(until).getTime() - now) / 1000));
}

function Conduct({ live, setLive, reload }: { live: LiveData; setLive: (l: LiveData) => void; reload: () => void }) {
  const { t, lang, locale } = useI18n();
  const announce = useAnnounce();
  const left = useCountdown(live.expires_at);
  const stage = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);
  const [predictOpen, setPredictOpen] = useState(false);
  const url = `${window.location.origin}/c/${live.code}`;
  const safety = live.safety_net?.[lang]?.trim() || t['safety.message'];

  // Count only, every 5 seconds while open. When the time runs out, reload
  // to show the results.
  useEffect(() => {
    const id = window.setInterval(async () => {
      const r = await rpc<LiveData>('teacher_session_live', { p_session_id: live.session_id });
      if (!r.error) setLive(r.data);
    }, 5000);
    return () => window.clearInterval(id);
  }, [live.session_id, setLive]);
  useEffect(() => {
    if (left === 0) reload();
  }, [left, reload]);

  const canExtend = new Date(live.expires_at).getTime() < new Date(live.can_extend_until).getTime() - 30000;

  async function extend(min: number) {
    setErr(null);
    const r = await rpc<{ expires_at: string }>('teacher_extend_session', { p_session_id: live.session_id, p_minutes: min });
    if (r.error) setErr(r.error);
    else {
      setLive({ ...live, expires_at: r.data.expires_at });
      announce(t['ts.extended'](new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(r.data.expires_at))));
    }
  }

  async function close() {
    setBusy(true);
    const r = await rpc('teacher_close_session', { p_session_id: live.session_id });
    setBusy(false);
    if (r.error) {
      setErr(r.error);
      return;
    }
    announce(t['ts.closedNow']);
    reload();
  }

  function fullscreen() {
    const el = stage.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else el.requestFullscreen?.().catch(() => undefined);
  }

  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');

  return (
    <>
      <p className="crumbs"><Link to="/teacher">← {t['teacher.title']}</Link></p>
      <div className="projector" ref={stage}>
        <div className="projector-main">
          <p className="projector-class">{live.class_label}</p>
          <p className="projector-lead">{t['ts.projLead']}</p>
          <p className="projector-url">{url.replace(/^https?:\/\//, '')}</p>
          <p className="session-code projector-code">
            <VisuallyHidden>{t['session.codeLabel']}: {live.code.split('').join(' ')}</VisuallyHidden>
            {live.code.split('').map((ch, i) => (
              <span key={i} aria-hidden="true">{ch}</span>
            ))}
          </p>
          <p className="projector-time" aria-hidden="true">{t['ts.timeLeft'](`${mm}:${ss}`)}</p>
        </div>
        <div className="projector-qr">
          <QrCode value={url} label={t['ts.qrLabel'](live.code.split('').join(' '))} />
        </div>
        <aside className="projector-safety" aria-label={t['safety.label']}>
          <span aria-hidden="true">💛</span> {safety}
        </aside>
      </div>

      <div className="two-col">
        <section className="panel p5" aria-labelledby="live-title">
          <h2 className="panel-title" id="live-title">{t['ts.liveTitle']}</h2>
          <p className="live-count" aria-live="polite">
            <strong>{live.count}</strong> {t['ts.liveCount'](live.count)}
          </p>
          <p className="form-hint">{t['ts.liveHint']}</p>
          <p className="text-muted">{t['ts.endsAt'](new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(live.expires_at)))}</p>
          {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={fullscreen}>{t['ts.fullscreen']}</button>
            {canExtend && (
              <>
                <button type="button" className="btn btn-ghost" onClick={() => extend(5)}>{t['ts.extend'](5)}</button>
                <button type="button" className="btn btn-ghost" onClick={() => extend(10)}>{t['ts.extend'](10)}</button>
              </>
            )}
          </div>
          {!canExtend && <p className="form-hint">{t['ts.atLimit'](live.max_minutes)}</p>}
          <p className="form-hint">{t['ts.closeIntro'](live.count)}</p>
          <SlideToEnd onEnd={close} busy={busy} />
        </section>
        <section className="panel p5" aria-labelledby="pred-title">
          <h2 className="panel-title" id="pred-title">{t['ts.predictTitleShort']}</h2>
          {live.prediction ? (
            <p>{t['ts.youExpected'](live.prediction.expected_mood)}</p>
          ) : (
            <p className="text-muted">{t['ts.noPrediction']}</p>
          )}
          <p className="form-hint">{t['ts.predictHint']}</p>
          <button type="button" className="link-btn" onClick={() => setPredictOpen(true)}>
            {live.prediction ? t['ts.predictChange'] : t['ts.predictAdd']}
          </button>
        </section>
      </div>

      <PredictDialog live={live} open={predictOpen} onClose={() => setPredictOpen(false)} onSaved={reload} />
    </>
  );
}

function PredictDialog({ live, open, onClose, onSaved }: { live: LiveData; open: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const [mood, setMood] = useState<number | null>(live.prediction?.expected_mood ?? null);
  const [topics, setTopics] = useState<string[]>(live.prediction?.expected_topics ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);
  async function save() {
    if (mood === null) return;
    setBusy(true);
    const r = await rpc('teacher_set_prediction', { p_session_id: live.session_id, p_expected_mood: mood, p_expected_topics: topics });
    setBusy(false);
    if (r.error) setErr(r.error);
    else {
      onClose();
      onSaved();
    }
  }
  return (
    <Dialog
      open={open}
      title={t['ts.predictTitle']}
      intro={<p>{t['ts.predictHint']}</p>}
      submitLabel={t['ts.save']}
      busy={busy}
      submitDisabled={mood === null}
      error={err ? errorText(t, err) : null}
      onSubmit={save}
      onClose={onClose}
      wide
    >
      <fieldset className="fieldset">
        <legend className="field-label">{t['ts.predictMood']}</legend>
        <div className="chip-row">
          {MOOD_SCALE.map((m) => (
            <label key={m.value} className={`chip-check${mood === m.value ? ' on' : ''}`}>
              <input type="radio" name="dlg-mood" value={m.value} checked={mood === m.value} onChange={() => setMood(m.value)} />
              <span aria-hidden="true">{m.emoji}</span> {t[`mood.${m.value}` as 'mood.1']}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="fieldset">
        <legend className="field-label">{t['ts.predictTopics']}</legend>
        <div className="chip-row">
          {TOPIC_TAGS.filter((tt) => live.topics.includes(tt.value)).map((tt) => {
            const on = topics.includes(tt.value);
            return (
              <label key={tt.value} className={`chip-check${on ? ' on' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => setTopics((p) => (on ? p.filter((x) => x !== tt.value) : [...p, tt.value]))} />
                <span aria-hidden="true">{tt.emoji}</span> {t[`topic.${tt.value}` as 'topic.exams']}
              </label>
            );
          })}
        </div>
      </fieldset>
    </Dialog>
  );
}

// ── Reflect ────────────────────────────────────────────────────────────────

function Reflect({ sessionId, classId }: { sessionId: string; classId: string }) {
  const { t, locale } = useI18n();
  const [data, setData] = useState<SessionResults | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<SessionResults>('teacher_session_results', { p_session_id: sessionId });
    if (r.error) setErr(r.error);
    else setData(r.data);
  }, [sessionId]);
  useEffect(() => {
    load();
  }, [load]);

  if (err) return <p className="error-text" role="alert">{errorText(t, err)}</p>;
  if (!data) return <p role="status">{t.loading}</p>;

  const s = data.session;
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  return (
    <>
      <p className="crumbs">
        <Link to="/teacher">← {t['teacher.title']}</Link> · <Link to={`/teacher/class/${classId}`}>{t['ts.classTitle'](s.class_label)}</Link>
      </p>
      <section className="panel p5" aria-labelledby="sess-head">
        <h2 className="panel-title" id="sess-head">{fmt.format(new Date(s.started_at))}</h2>
        <p>
          {s.context_tag && <Pill kind="neutral">{t[`ts.tag.${s.context_tag}` as 'ts.tag.normal']}</Pill>}{' '}
          <span className="text-muted">{t['ts.questionSet'](s.question_set_version)}</span>
        </p>
        {data.safety_valve_flagged && (
          <p className="text-muted"><Pill kind="neutral">{t['r6.sr.safetyPill']}</Pill> {t['r6.sr.safetyNote']}</p>
        )}
      </section>
      {data.features.swipe_checkin !== false ? (
        <ReflectDeck data={data} sessionId={sessionId} reload={load} />
      ) : (
        <>
          <div className="two-col">
            <ResultsPanel results={data.results} topics={s.topics} />
            <div className="stack">
              <PredictionPanel data={data} />
              <YouSaidPanel data={data} reload={load} />
            </div>
          </div>
          <ActOnResults data={data} />
          <NotesPanel sessionId={sessionId} initial={data.note} />
        </>
      )}
    </>
  );
}

// Release 4b's results deck: the same five panels, repaginated into four
// slides with the same wizard-stepper pattern as the admin "Add a school"
// wizard. ActOnResults/YouSaidPanel/NotesPanel keep their own internal RPCs
// and mutations — only their position on the page changes.
const SLIDES = ['results', 'predict', 'act', 'notes'] as const;
type Slide = (typeof SLIDES)[number];

function ReflectDeck({ data, sessionId, reload }: { data: SessionResults; sessionId: string; reload: () => void }) {
  const { t } = useI18n();
  const s = data.session;
  const [slide, setSlide] = useState<Slide>('results');
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstSlide = useRef(true);

  // A different session (navigating between Reflect pages) starts its own
  // deck back at the first slide.
  useEffect(() => {
    setSlide('results');
    firstSlide.current = true;
  }, [sessionId]);

  useEffect(() => {
    if (firstSlide.current) {
      firstSlide.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [slide]);

  const idx = SLIDES.indexOf(slide);
  const slideTitle: Record<Slide, string> = {
    results: t['r4b.ts.slideResults'],
    predict: t['r4b.ts.slidePredict'],
    act: t['r4b.ts.slideAct'],
    notes: t['r4b.ts.slideNotes'],
  };

  return (
    <>
      <section className="panel p5" aria-labelledby="ts-deck-step">
        <ol className="stepper sx-ts-stepper" aria-label={t['pa.wizSteps']}>
          {SLIDES.map((sl, i) => (
            <li key={sl} className={i === idx ? 'on' : i < idx ? 'done' : ''} aria-current={i === idx ? 'step' : undefined}>
              <span className="stepper-n" aria-hidden="true">{i < idx ? '✓' : i + 1}</span>
              <span>{slideTitle[sl]}</span>
              {i < idx && <span className="visually-hidden"> ({t['pa.done']})</span>}
            </li>
          ))}
        </ol>
        <h2 className="panel-title" id="ts-deck-step" ref={stepHeading} tabIndex={-1}>
          {t['pa.wizStepOf'](idx + 1, SLIDES.length, slideTitle[slide])}
        </h2>
      </section>

      {slide === 'results' && (
        <div className="two-col">
          <ResultsPanel results={data.results} topics={s.topics} />
          <ConcernBreakdownPanel breakdown={data.concern_breakdown} />
        </div>
      )}
      {slide === 'predict' && (
        <div className="two-col">
          <PredictionPanel data={data} />
          <YouSaidPanel data={data} reload={reload} />
        </div>
      )}
      {slide === 'act' && <ActOnResults data={data} />}
      {slide === 'notes' && <NotesPanel sessionId={sessionId} initial={data.note} />}

      <section className="panel p5 ts-deck-nav">
        <div className="form-actions">
          {idx > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => setSlide(SLIDES[idx - 1])}>
              {t['pa.back']}
            </button>
          )}
          {idx < SLIDES.length - 1 && (
            <button type="button" className="btn btn-primary" onClick={() => setSlide(SLIDES[idx + 1])}>
              {t['pa.next']}
            </button>
          )}
        </div>
      </section>
    </>
  );
}

function ConcernBreakdownPanel({ breakdown }: { breakdown: ConcernBreakdown }) {
  const { t } = useI18n();
  return (
    <section className="panel p5 class-card" aria-labelledby="concern-title">
      <h2 className="panel-title" id="concern-title">{t['r4b.ts.concernTitle']}</h2>
      {breakdown.status === 'insufficient' ? (
        <Suppressed title={t['sup.insufficientTitle']}>{t['r4b.ts.concernInsufficient']}</Suppressed>
      ) : (
        <Bars
          labelledBy="concern-title"
          total={breakdown.total}
          data={CONCERN_CODES.map((c) => ({
            key: c.value,
            emoji: c.emoji,
            label: t[`concern.${c.value}` as 'concern.unspecified'],
            count: breakdown.concern_counts[c.value] ?? null,
          }))}
        />
      )}
    </section>
  );
}

function ResultsPanel({ results, topics }: { results: Breakdown; topics: string[] }) {
  const { t } = useI18n();
  const shownTopics = TOPIC_TAGS.filter((tt) => topics.includes(tt.value));
  return (
    <section className="panel p5 class-card" aria-labelledby="res-title">
      <h2 className="panel-title" id="res-title">{t['ts.resultsTitle']}</h2>
      {results.status === 'insufficient' && (
        <Suppressed title={t['sup.insufficientTitle']}>{t['ts.belowFloor'](results.total, results.floor)}</Suppressed>
      )}
      {results.status === 'suppressed_near_unanimous' && (
        <Suppressed title={t['sup.unanimousTitle']}>{t['sup.unanimousClass'](results.total)}</Suppressed>
      )}
      {results.status === 'ok' && (
        <>
          <div className="score-line" role="group" aria-label={t['results.average']}>
            <ScoreValue score={results.mean} className="score-num" />
            <Pill kind={bandOf(results.mean).key}>{t[`band.${bandOf(results.mean).key}`]}</Pill>
            <span className="text-muted" style={{ marginLeft: 'auto' }}>{t['results.responses'](results.total)}</span>
          </div>
          <div>
            <h3 className="overline" id="res-mood">{t['results.mood']}</h3>
            <MoodSplit labelledBy="res-mood" result={results} />
          </div>
          <div>
            <h3 className="overline" id="res-topics">{t['results.topics']}</h3>
            <Bars
              labelledBy="res-topics"
              total={results.total}
              data={shownTopics.map((tag) => {
                const n = results.topic_counts[tag.value];
                return { key: tag.value, emoji: tag.emoji, label: t[`topic.${tag.value}` as 'topic.exams'], count: n ?? null };
              })}
            />
            {shownTopics.some((tag) => results.topic_counts[tag.value] == null) && <p className="bars-note">{t['results.hiddenNote']}</p>}
          </div>
          {results.by_topic && (
            <div>
              <h3 className="overline">{t['ts.byTopic']}</h3>
              <ul className="crosstab-list">
                {shownTopics.map((tag) => {
                  const x = results.by_topic?.[tag.value];
                  if (!x) return null;
                  return (
                    <li key={tag.value}>
                      <span><span aria-hidden="true">{tag.emoji} </span>{t[`topic.${tag.value}` as 'topic.exams']}</span>
                      {x.status === 'ok' ? (
                        <span>{t['ts.crossMean'](x.mean != null ? x.mean.toFixed(1) : meanOf(x.mood_counts ?? {}), x.total)}</span>
                      ) : (
                        <span className="text-muted">{t['ts.crossHidden']}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="bars-note">{t['ts.crossNote']}</p>
            </div>
          )}
        </>
      )}
      <p className="form-hint">{t['ts.resultsWho']}</p>
    </section>
  );
}

function meanOf(c: Record<string, number>): string {
  let s = 0;
  let n = 0;
  for (const [k, v] of Object.entries(c)) {
    s += Number(k) * v;
    n += v;
  }
  return n ? (s / n).toFixed(1) : '–';
}

function PredictionPanel({ data }: { data: SessionResults }) {
  const { t, num } = useI18n();
  const p = data.prediction;
  const r = data.results;
  return (
    <section className="panel p5" aria-labelledby="cmp-title">
      <h2 className="panel-title" id="cmp-title">{t['ts.compareTitle']}</h2>
      {!p ? (
        <p className="text-muted">{t['ts.noPredictionMade']}</p>
      ) : r.status !== 'ok' ? (
        <p>{t['ts.youExpected'](p.expected_mood)} {t['ts.compareHidden']}</p>
      ) : (
        <dl className="kv">
          <div>
            <dt>{t['ts.expected']}</dt>
            <dd>{p.expected_mood}/5</dd>
          </div>
          <div>
            <dt>{t['ts.classSaid']}</dt>
            <dd>{num(r.mean)}/5</dd>
          </div>
          <div>
            <dt>{t['ts.gap']}</dt>
            <dd>{gapText(t, p.expected_mood - r.mean, num)}</dd>
          </div>
          {p.expected_topics.length > 0 && (
            <div>
              <dt>{t['ts.expectedTopics']}</dt>
              <dd>{p.expected_topics.map((x) => t[`topic.${x}` as 'topic.exams']).join(', ')}</dd>
            </div>
          )}
        </dl>
      )}
      <p className="form-hint">{t['ts.onlyYou']}</p>
      <p><Link className="ts-link" to="/teacher/calibration">{t['ts.navCalibration']}</Link></p>
    </section>
  );
}

function gapText(t: ReturnType<typeof useI18n>['t'], gap: number, num: (n: number) => string): string {
  if (Math.abs(gap) < 0.25) return t['ts.gapClose'];
  return gap > 0 ? t['ts.gapHigher'](num(Math.abs(gap))) : t['ts.gapLower'](num(Math.abs(gap)));
}

function fmtDay(locale: string, iso: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(new Date(iso));
}

function YouSaidPanel({ data, reload }: { data: SessionResults; reload: () => void }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const [body, setBody] = useState('');
  const [item, setItem] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);

  async function post(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await rpc('teacher_post_you_said', { p_session_id: data.session.id, p_body: body });
    setBusy(false);
    if (r.error) setErr(r.error);
    else {
      announce(t['ts.ysPosted']);
      setBody('');
      reload();
    }
  }
  // K6 (B18): add this week's action to the same reply instead of reposting.
  async function addItem(e: FormEvent) {
    e.preventDefault();
    if (!data.you_said) return;
    setBusy(true);
    setErr(null);
    const r = await rpc('teacher_add_you_said', { p_message_id: data.you_said.id, p_body: item });
    setBusy(false);
    if (r.error) setErr(r.error);
    else {
      announce(t['r4.ys.addedOk']);
      setItem('');
      reload();
    }
  }
  async function withdraw(id: string) {
    const r = await rpc('teacher_withdraw_you_said', { p_message_id: id });
    if (r.error) setErr(r.error);
    else {
      announce(t['ts.ysWithdrawn']);
      reload();
    }
  }

  return (
    <section className="panel p5" aria-labelledby="ys-title">
      <h2 className="panel-title" id="ys-title">{t['ts.ysTitle']}</h2>
      {data.you_said ? (
        <>
          <blockquote className="ys-quote">{data.you_said.body}</blockquote>
          {data.you_said.items && data.you_said.items.length > 0 && (
            <>
              <p className="field-label">{t['r4.ys.added']}</p>
              <ul className="ys-items">
                {[...data.you_said.items].reverse().map((it) => (
                  <li key={it.at}>{it.body}</li>
                ))}
              </ul>
            </>
          )}
          <p className="form-hint">
            {data.you_said.visible_until ? t['r4.ys.visibleUntil'](fmtDay(locale, data.you_said.visible_until)) : t['ts.ysShown']}
          </p>
          {data.you_said.can_add && (
            <form className="form" onSubmit={addItem} noValidate>
              <div className="field-group">
                <label className="field-label" htmlFor="ys-add">{t['r4.ys.add']}</label>
                <textarea id="ys-add" className="field" rows={2} maxLength={200} value={item}
                  onChange={(e) => setItem(e.target.value)} aria-describedby="ys-add-hint" />
                <p className="form-hint" id="ys-add-hint">{t['r4.ys.addHint']} {t['ts.charsLeft'](200 - item.length)}</p>
              </div>
              {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
              <div className="form-actions">
                <button type="submit" className="btn btn-ghost" disabled={busy || item.trim().length < 3}>{t['r4.ys.addButton']}</button>
              </div>
            </form>
          )}
          <button type="button" className="link-btn" onClick={() => withdraw(data.you_said!.id)}>{t['ts.ysWithdraw']}</button>
        </>
      ) : data.can_post_you_said ? (
        <form className="form" onSubmit={post} noValidate>
          <div className="field-group">
            <label className="field-label" htmlFor="ys-body">{t['ts.ysLabel']}</label>
            <textarea
              id="ys-body"
              className="field"
              rows={2}
              maxLength={200}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t['ts.ysPlaceholder']}
              aria-describedby="ys-hint"
            />
            <p className="form-hint" id="ys-hint">{t['ts.ysRule']} {t['ts.charsLeft'](200 - body.length)}</p>
          </div>
          {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || body.trim().length < 3}>{t['ts.ysPost']}</button>
          </div>
        </form>
      ) : (
        <p className="text-muted">{t['ts.ysNotYet']}</p>
      )}
    </section>
  );
}

function NotesPanel({ sessionId, initial }: { sessionId: string; initial: string | null }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const [body, setBody] = useState(initial ?? '');
  const [saved, setSaved] = useState(initial ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const r = await rpc('teacher_save_note', { p_session_id: sessionId, p_body: body });
    setBusy(false);
    if (r.error) setErr(r.error);
    else {
      setSaved(body);
      announce(t['ts.noteSaved']);
    }
  }
  return (
    <section className="panel p5" aria-labelledby="note-title">
      <h2 className="panel-title" id="note-title">{t['ts.noteTitle']}</h2>
      <form className="form" onSubmit={save} noValidate>
        <div className="field-group">
          <label className="field-label" htmlFor="note-body">{t['ts.noteLabel']}</label>
          <textarea id="note-body" className="field" rows={4} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} aria-describedby="note-hint" />
          <p className="form-hint" id="note-hint">{t['ts.noteWho']}</p>
        </div>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          <button type="submit" className="btn btn-ghost" disabled={busy || body === saved}>{t['ts.save']}</button>
        </div>
      </form>
    </section>
  );
}
